'use client';

import { useSession } from '@impact/auth/client';
import type { AuthMeResponse } from '@impact/types';
import { trpc } from '@/lib/trpc';
import type { AdminSession } from '@/lib/server-context';
import { useTenant } from '@/hooks/use-tenant';

export interface UseAuthResult {
  authContext: AuthMeResponse | null;
  isAuthenticated: boolean;
  isHydrating: boolean;
  user: AuthMeResponse['user'] | null;
  userType: AuthMeResponse['userType'] | null;
  entrant: AuthMeResponse['entrant'];
  eventAdmin: AuthMeResponse['eventAdmin'];
  eventAdmins: AuthMeResponse['eventAdmins'];
  registrationStatus: AuthMeResponse['registrationStatus'];
  redirectUrl: string | null;
  isEntrant: () => boolean;
  isEventAdmin: () => boolean;
}

interface UseAuthOptions {
  hydrateFromQuery?: boolean;
}

export function useAuth({ hydrateFromQuery = false }: UseAuthOptions = {}): UseAuthResult {
  const tenantId = useTenant();
  const { session } = useSession<AdminSession>();
  const authQuery = trpc.authMe.me.useQuery(undefined, {
    enabled: hydrateFromQuery && !session && !!tenantId,
    retry: false,
    staleTime: 60_000,
  });
  const authContext = session?.authContext ?? authQuery.data ?? null;
  const isHydrating = hydrateFromQuery && !session && !!tenantId && authQuery.isPending;

  return {
    authContext,
    isAuthenticated: !!session || !!authContext,
    isHydrating,
    user: authContext?.user ?? null,
    userType: authContext?.userType ?? null,
    entrant: authContext?.entrant ?? null,
    eventAdmin: authContext?.eventAdmin ?? null,
    eventAdmins: authContext?.eventAdmins ?? [],
    registrationStatus: authContext?.registrationStatus ?? null,
    redirectUrl: authContext?.redirectUrl ?? null,
    isEntrant: () => authContext?.userType === 'entrant',
    isEventAdmin: () => authContext?.userType === 'event_admin',
  };
}
